import { useEffect, useRef } from 'react';

interface VideoBlockProps {
  src: string;
  objectFit?: 'contain' | 'cover';
  rotation?: number;
  pageId?: number;
  boundryId?: string | null;
  isActive?: boolean;
  loop?: boolean;
  onEnded?: () => void;
}

const rotationStyle = {
  [180]: {
    transform: 'rotate(180deg)',
  },
  [90]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(90deg) translateY(-100%)',
    WebkitTransform: 'rotate(90deg) translateY(-100%)',
    MozTransform: 'rotate(90deg) translateY(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
  [270]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(270deg) translateX(-100%)',
    WebkitTransform: 'rotate(270deg) translateX(-100%)',
    MozTransform: 'rotate(270deg) translateX(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
} as const;

function VideoBlock({ src, objectFit, rotation = 0, isActive, loop = true, onEnded }: VideoBlockProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPlayRetry = () => {
    if (!playRetryTimeoutRef.current) {
      return;
    }

    clearTimeout(playRetryTimeoutRef.current);
    playRetryTimeoutRef.current = null;
  };

  const prepareForAutoplay = (video: HTMLVideoElement) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
  };

  const tryPlay = (video: HTMLVideoElement, retryOnAbort = true) => {
    if (!video.isConnected) {
      return;
    }

    const playResult = video.play();

    if (!playResult || typeof playResult.catch !== 'function') {
      return;
    }

    playResult.catch(err => {
      const isAbort = err?.name === 'AbortError';

      if (isAbort && retryOnAbort) {
        clearPlayRetry();
        playRetryTimeoutRef.current = setTimeout(() => {
          if (!video.isConnected || !video.paused) {
            return;
          }
          tryPlay(video, false);
        }, 80);
        return;
      }

      console.warn('Video playback failed:', err);
    });
  };

  const resetVideo = (video: HTMLVideoElement) => {
    if (!video.paused) {
      video.pause();
    }

    try {
      video.currentTime = 0;
    } catch {
      // Ignore reset errors on partially loaded media.
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    clearPlayRetry();
    prepareForAutoplay(video);

    if (isActive === false) {
      resetVideo(video);
      return () => {
        clearPlayRetry();
      };
    }

    video.load();
    return () => {
      clearPlayRetry();
    };
  }, [isActive, src]);

  // Only render the <video> element for the active page.
  // Samsung Tizen only supports a single hardware video decoder - multiple
  // <video> elements in the DOM simultaneously cause decoder conflicts.
  if (isActive === false) {
    return <div className="w-full h-full relative" />;
  }

  return (
    <div className="w-full h-full relative">
      <video
        ref={videoRef}
        controls={false}
        autoPlay
        playsInline
        loop={loop}
        preload="auto"
        muted
        onEnded={() => {
          if (!loop) {
            onEnded?.();
          }
        }}
        onCanPlay={() => {
          const video = videoRef.current;
          if (!video || !video.paused) {
            return;
          }

          prepareForAutoplay(video);
          tryPlay(video, false);
        }}
        className="w-full h-full"
        style={{
          objectFit: objectFit ?? 'contain',
          backgroundColor: 'transparent',
          ...(rotation !== 0 ? rotationStyle[rotation.toString() as unknown as keyof typeof rotationStyle] : {}),
        }}
      >
        <source src={src} type="video/mp4" />
        <source src={src} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

export default VideoBlock;
